import { api } from './api';

/* Mirrors Eduvora.Application.Invigilation. */

export interface PaperView {
  academicClassId: string | null;
  classLabel: string;
  subjectName: string;
  examName: string;
  /** Children sitting this paper. */
  sitting: number;
}

export interface RoomView {
  roomId: string;
  roomName: string;
  capacity: number;
  classLabel: string | null;
  subjectName: string | null;
  seated: number;
  invigilatorsNeeded: number;
}

export interface AssignmentView {
  employeeId: string;
  fullName: string;
  designation: string | null;
  role: string;
  roomId: string | null;
  roomName: string | null;
  classLabel: string | null;
  subjectName: string | null;
  /** ⚠ Null when no rule was bent. A sentence when one was — render it, never hide it. */
  compromise: string | null;
  /** Set only when someone else on this chart has the same name. See AssignmentView.Code. */
  code: string | null;
}

export interface UnfilledView { role: string; roomName: string | null; reason: string }
export interface LoadView { employeeId: string; fullName: string; duties: number }
export interface InvigilationOption { id: string; name: string }

export interface InvigilationDay {
  date: string;
  shift: string;
  institutionId: string | null;
  institutions: InvigilationOption[];
  examDates: string[];

  papers: PaperView[];
  rooms: RoomView[];
  assignments: AssignmentView[];
  unfilled: UnfilledView[];
  busiest: LoadView[];

  assigned: number;
  compromised: number;
  candidatesAvailable: number;
  candidatesUnavailable: number;
  maxLoad: number;
  minLoad: number;
  seatShortfall: number;

  /** True when these rows came out of the database; false when they are an unsaved proposal. */
  isSaved: boolean;
  note: string | null;
}

export interface MyDutyView {
  date: string;
  shift: string;
  role: string;
  roomName: string | null;
  classLabel: string | null;
  subjectName: string | null;
  examName: string | null;
  compromise: string | null;
}

export interface PlanOptions {
  institutionId?: string | null;
  date: string;
  shift: string;
  relieving: number;
  squad: number;
  maxDuties: number;
  avoidOwnSubject: boolean;
  avoidOwnClass: boolean;
}

function qs(o: PlanOptions, regenerate: boolean): string {
  const p = new URLSearchParams();
  if (o.institutionId) p.set('institutionId', o.institutionId);
  p.set('date', o.date);
  p.set('shift', o.shift);
  p.set('relieving', String(o.relieving));
  p.set('squad', String(o.squad));
  p.set('maxDuties', String(o.maxDuties));
  p.set('avoidOwnSubject', String(o.avoidOwnSubject));
  p.set('avoidOwnClass', String(o.avoidOwnClass));
  p.set('regenerate', String(regenerate));
  return p.toString();
}

export const invigilationApi = {
  day: (o: PlanOptions, regenerate: boolean) =>
    api.get<InvigilationDay>(`/api/invigilation/day?${qs(o, regenerate)}`),

  /* ⚠ Sends the PARAMETERS, not the chart. The server runs the planner again — see
     InvigilationEndpoints for why trusting a client-supplied roster is a mistake. */
  publish: (o: PlanOptions) =>
    api.post<{ published: number }>('/api/invigilation/publish', {
      institutionId: o.institutionId ?? null,
      date: o.date,
      shift: o.shift,
      relieving: o.relieving,
      squad: o.squad,
      maxDuties: o.maxDuties,
      avoidOwnSubject: o.avoidOwnSubject,
      avoidOwnClass: o.avoidOwnClass,
    }),

  clear: (institutionId: string | null, date: string, shift: string) =>
    api.post<{ cleared: number }>('/api/invigilation/clear', { institutionId, date, shift }),

  mine: (from?: string, days = 30) =>
    api.get<MyDutyView[]>(
      `/api/invigilation/mine?${new URLSearchParams({ ...(from ? { from } : {}), days: String(days) })}`,
    ),
};

/** ⚠ Local date. `toISOString()` shifts to UTC and hands back yesterday for half of India's day. */
export function isoDate(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export const SHIFTS = ['Morning', 'Afternoon', 'Evening'] as const;

/** "RoomInCharge" reads like a database column. Nobody writes that on a noticeboard. */
export function roleLabel(role: string): string {
  switch (role) {
    case 'RoomInCharge': return 'Room in charge';
    case 'Invigilator': return 'Invigilator';
    case 'Relieving': return 'Relieving';
    case 'Squad': return 'Squad';
    default: return role;
  }
}
